// The grid itself (design 02 9, 12.1): only the cells in view are drawn
// (rows and columns), column letters and row numbers, frozen rows and
// columns, the selection, column widths dragged on a PC, and the cell
// editor. The scroll box holds a sized box (for the scroll bars) with a
// sticky view the size of the screen; everything is drawn in that view at
// positions computed from the scroll offsets, so frozen panes are just
// cells that do not move.
//
// The editor is one <textarea> (Korean input, design 8.7): on a PC it has
// the focus while the grid has it, invisible over the active cell, so that
// typing (and composing Hangul) goes into it and starts editing; the screen
// above (Grid.tsx) handles its keys. Phones edit in the formula bar at the
// bottom: a finger picks a cell when it lifts (a tap; a finger that moves
// scrolls and picks nothing), the text being typed shows over the cell, and
// while it is typed a tap keeps the keyboard (the screen decides what the
// tap does: the next cell, or a reference in a formula).
import { type ReactNode, type RefObject, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import type { TensorSheet } from '../api'
import { FLAG } from '../calc/protocol'
import { formatValue } from '../grid/format'
import { COL_W, HEAD_H, HEAD_W, ROW_H, type Rect, type Sel, areaSel, colAt, colName, colPositions, rectOf } from '../grid/layout'
import { GRID_COLS, GRID_ROWS } from '../grid/model'
import type { GridStore } from '../grid/store'

export interface ViewInfo {
  sx: number
  sy: number
  vw: number
  vh: number
  xs: Float64Array
  frozenW: number
  frozenH: number
}

export type HitArea = 'cell' | 'col' | 'row' | 'corner'

export interface CanvasProps {
  store: GridStore
  sheet: TensorSheet
  widths: Record<string, number>
  sel: Sel
  editing: boolean
  editorRef: RefObject<HTMLTextAreaElement | null>
  editorProps: React.TextareaHTMLAttributes<HTMLTextAreaElement>
  /** a fine pointer (PC): drag to select, resize columns, the editor in the cell */
  fine: boolean
  onSelect: (sel: Sel, how: { drag: boolean; area: HitArea; double?: boolean }) => void
  /** a finger's tap (a touch that did not scroll), on a cell or a header */
  onTap: (r: number, c: number, area: HitArea) => void
  /** typing in the formula bar on a phone: a touch keeps the focus there (and the keyboard up) */
  keepFocus: boolean
  /** the text being typed in the formula bar, shown over the active cell */
  preview: string | null
  /** the cells a tap put into the formula being typed */
  pointRect: Rect | null
  onMenu: (x: number, y: number, area: HitArea, row: number, col: number) => void
  onResize: (col: number, width: number, done: boolean) => void
  overlay?: (v: ViewInfo) => ReactNode
  /** asked to scroll so a cell is in view */
  reveal: { r: number; c: number; n: number } | null
}

export function GridCanvas(p: CanvasProps) {
  const { store, sheet, sel, fine } = p
  const scroller = useRef<HTMLDivElement>(null)
  const view = useRef<HTMLDivElement>(null)
  const [scroll, setScroll] = useState({ sx: 0, sy: 0, vw: 800, vh: 600 })
  const raf = useRef(0)

  const used = store.used(sheet.id)
  const fr = Math.min(sheet.frozen_rows, 100)
  const fc = Math.min(sheet.frozen_cols, 50)
  const selRect = rectOf(sel)
  const colCount = Math.min(
    GRID_COLS,
    Math.max(26, used.cols + 12, fc + 12, (selRect.c2 < GRID_COLS - 1 ? selRect.c2 : sel.ac) + 6),
  )
  const xs = useMemo(() => colPositions(p.widths, colCount), [p.widths, colCount])
  const frozenW = xs[fc] ?? 0
  const frozenH = fr * ROW_H
  const rowCount = Math.min(
    GRID_ROWS,
    Math.max(
      100,
      used.rows + 60,
      fr + 60,
      (selRect.r2 < GRID_ROWS - 1 ? selRect.r2 : sel.ar) + 30,
      Math.ceil((scroll.sy + scroll.vh) / ROW_H) + fr + 40,
    ),
  )
  const totalW = HEAD_W + (xs[colCount] ?? 0)
  const totalH = HEAD_H + rowCount * ROW_H

  const measure = useCallback(() => {
    const el = scroller.current
    if (!el) return
    setScroll((s) => {
      const n = { sx: el.scrollLeft, sy: el.scrollTop, vw: el.clientWidth, vh: el.clientHeight }
      return n.sx === s.sx && n.sy === s.sy && n.vw === s.vw && n.vh === s.vh ? s : n
    })
  }, [])

  useLayoutEffect(() => {
    measure()
    const el = scroller.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(() => measure())
    ro.observe(el)
    return () => ro.disconnect()
  }, [measure])

  // a new sheet starts at its top left
  useLayoutEffect(() => {
    const el = scroller.current
    if (!el) return
    el.scrollTop = 0
    el.scrollLeft = 0
    measure()
  }, [sheet.id, measure])

  const onScroll = () => {
    cancelAnimationFrame(raf.current)
    raf.current = requestAnimationFrame(measure)
  }

  // x, y of a cell's top left in the view
  const cx = (c: number) => HEAD_W + (c < fc ? (xs[c] ?? 0) : (xs[c] ?? c * COL_W) - scroll.sx)
  const cy = (r: number) => HEAD_H + (r < fr ? r * ROW_H : r * ROW_H - scroll.sy)
  const cw = (c: number) => (xs[c + 1] ?? 0) - (xs[c] ?? 0) || COL_W

  // scroll a cell into view (keys, find)
  useEffect(() => {
    const el = scroller.current
    const target = p.reveal
    if (!el || !target) return
    const { r, c } = target
    const bodyW = el.clientWidth - HEAD_W - frozenW
    const bodyH = el.clientHeight - HEAD_H - frozenH
    if (r >= fr) {
      const top = r * ROW_H - frozenH
      if (top < el.scrollTop) el.scrollTop = top
      else if (top + ROW_H > el.scrollTop + bodyH) el.scrollTop = top + ROW_H - bodyH
    }
    if (c >= fc && c < colCount) {
      const left = (xs[c] ?? 0) - frozenW
      const w = cw(c)
      if (left < el.scrollLeft) el.scrollLeft = left
      else if (left + w > el.scrollLeft + bodyW) el.scrollLeft = left + w - bodyW
    }
    measure()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [p.reveal])

  // what is in view
  const bodyRows = (() => {
    const first = fr + Math.floor(scroll.sy / ROW_H)
    const last = Math.min(rowCount - 1, fr + Math.ceil((scroll.sy + scroll.vh) / ROW_H))
    return [first, last] as const
  })()
  const bodyCols = (() => {
    const first = Math.max(fc, colAt(xs, frozenW + scroll.sx))
    const last = Math.min(colCount - 1, colAt(xs, frozenW + scroll.sx + scroll.vw))
    return [first, last] as const
  })()
  const rowsIn = (a: number, b: number) => {
    const out: number[] = []
    for (let r = a; r <= b; r++) out.push(r)
    return out
  }
  const frozenRows = rowsIn(0, fr - 1)
  const frozenCols = rowsIn(0, fc - 1)
  const rows = rowsIn(bodyRows[0], bodyRows[1])
  const cols = rowsIn(bodyCols[0], bodyCols[1])

  const renderCell = (r: number, c: number) => {
    const cell = store.cell(sheet.id, r, c)
    const left = cx(c)
    const top = cy(r)
    const width = cw(c)
    if (!cell) return <div key={`${r}:${c}`} className="g-c" data-cell={`${colName(c)}${r + 1}`} style={{ left, top, width }} />
    const st = cell.style
    const flags = cell.flags
    // a spilled cell without a format of its own shows its anchor's
    const nf = st?.nf ?? (flags & FLAG.spilled ? spillFormat(r, c) : undefined)
    const shown = formatValue(cell.value, nf, cell.input)
    const align = st?.al ?? shown.align
    // text runs on over empty cells to its right (as Excel)
    let wide = width
    if (align === 'l' && typeof cell.value === 'string' && !st?.wrap && c >= fc) {
      for (let k = c + 1; k < Math.min(colCount, c + 12); k++) {
        const next = store.cell(sheet.id, r, k)
        if (next && (next.input !== '' || next.value !== null)) break
        wide += cw(k)
        if (wide > shown.text.length * 16) break
      }
    }
    const cls =
      'g-c' +
      (shown.error ? ' g-err' : '') +
      (cell.pending ? ' g-pend' : '') +
      (flags & FLAG.circular ? ' g-circ' : '') +
      (flags & (FLAG.spill | FLAG.spilled) ? ' g-spill' : '') +
      (st?.wrap ? ' g-wrap' : '') +
      (align === 'r' ? ' g-r' : align === 'c' ? ' g-ctr' : '')
    return (
      <div
        key={`${r}:${c}`}
        className={cls}
        data-cell={`${colName(c)}${r + 1}`}
        title={flags & FLAG.circular ? '순환 참조' : flags & FLAG.blocked ? '넘칠 자리에 다른 값이 있어요' : undefined}
        style={{
          left,
          top,
          width,
          ...(wide > width ? { overflow: 'visible', borderRightColor: 'transparent' } : {}),
          ...(st?.b ? { fontWeight: 700 } : {}),
          ...(st?.i ? { fontStyle: 'italic' } : {}),
          ...(st?.u ? { textDecoration: 'underline' } : {}),
          ...(st?.fg || shown.color ? { color: st?.fg ?? shown.color } : {}),
          ...(st?.bg ? { background: st.bg } : {}),
        }}
      >
        {wide > width ? (
          <span className="g-over" style={{ width: wide - 8 }}>
            {shown.text}
          </span>
        ) : (
          shown.text
        )}
      </div>
    )
  }

  /** The number format of the formula a spilled cell came from: up to the
      top of the spill in this column, then left to its anchor. */
  function spillFormat(r: number, c: number): string | undefined {
    const flagsAt = (rr: number, cc: number) => store.cell(sheet.id, rr, cc)?.flags ?? 0
    let rr = r
    while (rr > 0 && r - rr < 5000 && !(flagsAt(rr, c) & FLAG.spill) && flagsAt(rr - 1, c) & (FLAG.spilled | FLAG.spill)) rr--
    for (let cc = c; cc >= 0 && c - cc < 500; cc--) {
      const f = flagsAt(rr, cc)
      if (f & FLAG.spill) return store.cell(sheet.id, rr, cc)?.style?.nf
      if (!(f & FLAG.spilled)) return undefined
    }
    return undefined
  }

  const paneCells = (rs: number[], cs: number[]) => {
    const out: ReactNode[] = []
    for (const r of rs) for (const c of cs) out.push(renderCell(r, c))
    return out
  }

  // the selection in view coordinates
  const selBox = (rect: Rect) => {
    const x1 = cx(rect.c1)
    const y1 = cy(rect.r1)
    const x2 = rect.c2 >= colCount ? totalW : cx(Math.min(rect.c2, colCount - 1)) + cw(Math.min(rect.c2, colCount - 1))
    const y2 = rect.r2 >= rowCount ? HEAD_H + rowCount * ROW_H : cy(rect.r2) + ROW_H
    return { left: x1, top: y1, width: Math.max(0, x2 - x1), height: Math.max(0, y2 - y1) }
  }
  const active = { left: cx(sel.ac), top: cy(sel.ar), width: cw(sel.ac), height: ROW_H }
  const multi = selRect.r1 !== selRect.r2 || selRect.c1 !== selRect.c2

  const decorations = (
    <>
      {multi && <div className="g-sel" style={selBox(selRect)} />}
      {p.pointRect && <div className="g-point" style={selBox(p.pointRect)} />}
      <div className="g-active" style={active} />
      {p.preview !== null && (
        <div className="g-preview" style={{ left: active.left, top: active.top, minWidth: active.width, maxWidth: Math.max(active.width, scroll.vw - 24) }}>
          {p.preview}
        </div>
      )}
    </>
  )

  // panes: [left, top, width, height] of the view they show
  const bodyX = HEAD_W + frozenW
  const bodyY = HEAD_H + frozenH
  const panes: { key: string; clip: [number, number, number, number]; rs: number[]; cs: number[] }[] = [
    { key: 'br', clip: [bodyX, bodyY, scroll.vw - bodyX, scroll.vh - bodyY], rs: rows, cs: cols },
  ]
  if (fr) panes.push({ key: 'tr', clip: [bodyX, HEAD_H, scroll.vw - bodyX, frozenH], rs: frozenRows, cs: cols })
  if (fc) panes.push({ key: 'bl', clip: [HEAD_W, bodyY, frozenW, scroll.vh - bodyY], rs: rows, cs: frozenCols })
  if (fr && fc) panes.push({ key: 'tl', clip: [HEAD_W, HEAD_H, frozenW, frozenH], rs: frozenRows, cs: frozenCols })

  const inSelCols = (c: number) => c >= selRect.c1 && c <= selRect.c2
  const inSelRows = (r: number) => r >= selRect.r1 && r <= selRect.r2

  const colHead = (c: number) => (
    <div key={c} className={`g-ch${inSelCols(c) ? ' on' : ''}`} style={{ left: cx(c), width: cw(c) }}>
      {colName(c)}
      {fine && <div className="g-resize" data-resize={c} />}
    </div>
  )
  const rowHead = (r: number) => (
    <div key={r} className={`g-rh${inSelRows(r) ? ' on' : ''}`} style={{ top: cy(r) }}>
      {r + 1}
    </div>
  )

  // ---- pointer ----

  const hit = (clientX: number, clientY: number): { area: HitArea; r: number; c: number } => {
    const box = view.current!.getBoundingClientRect()
    const x = clientX - box.left
    const y = clientY - box.top
    const c = x < HEAD_W ? 0 : x < bodyX ? colAt(xs, x - HEAD_W) : colAt(xs, x - HEAD_W + scroll.sx)
    const r =
      y < HEAD_H
        ? 0
        : y < bodyY
          ? Math.floor((y - HEAD_H) / ROW_H)
          : Math.min(GRID_ROWS - 1, Math.floor((y - HEAD_H + scroll.sy) / ROW_H))
    if (x < HEAD_W && y < HEAD_H) return { area: 'corner', r: 0, c: 0 }
    if (y < HEAD_H) return { area: 'col', r: 0, c: Math.min(c, GRID_COLS - 1) }
    if (x < HEAD_W) return { area: 'row', r, c: 0 }
    return { area: 'cell', r, c: Math.min(c, GRID_COLS - 1) }
  }

  const selFor = (area: HitArea, r: number, c: number, from: Sel | null): Sel => {
    if (!from || area === 'corner') return areaSel(area, r, c)
    if (area === 'col') return { ...from, fr: GRID_ROWS - 1, fc: c }
    if (area === 'row') return { ...from, fr: r, fc: GRID_COLS - 1 }
    return { ...from, fr: r, fc: c }
  }

  // a finger's touch: a tap picks when it lifts (onClick); a scroll cancels it
  const tap = useRef(false)

  const onPointerDown = (e: React.PointerEvent) => {
    if (e.button === 2) return
    const target = e.target as HTMLElement
    tap.current = false
    if (target.closest('.g-overlay-item')) return
    if (e.pointerType === 'touch' || !fine) {
      tap.current = true
      // no mouse events after it: the formula bar keeps the focus
      if (p.keepFocus) e.preventDefault()
      return
    }
    const rz = target.getAttribute('data-resize')
    if (rz !== null && fine) {
      e.preventDefault()
      const c = Number(rz)
      const startX = e.clientX
      const startW = cw(c)
      const move = (ev: PointerEvent) => p.onResize(c, Math.max(20, Math.min(2000, Math.round(startW + ev.clientX - startX))), false)
      const up = (ev: PointerEvent) => {
        window.removeEventListener('pointermove', move)
        window.removeEventListener('pointerup', up)
        p.onResize(c, Math.max(20, Math.min(2000, Math.round(startW + ev.clientX - startX))), true)
      }
      window.addEventListener('pointermove', move)
      window.addEventListener('pointerup', up)
      return
    }
    if (target.closest('.g-editor')) return
    const h = hit(e.clientX, e.clientY)
    const extend = e.shiftKey && h.area !== 'corner'
    const first = selFor(h.area, h.r, h.c, extend ? sel : null)
    p.onSelect(first, { drag: false, area: h.area })
    e.preventDefault()
    let last = `${first.fr}:${first.fc}`
    const move = (ev: PointerEvent) => {
      const m = hit(ev.clientX, ev.clientY)
      const next = selFor(h.area === 'cell' ? 'cell' : h.area, m.r, m.c, first)
      const k = `${next.fr}:${next.fc}`
      if (k === last) return
      last = k
      p.onSelect(next, { drag: true, area: h.area })
    }
    const up = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', up)
    }
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', up)
  }

  const onClick = (e: React.MouseEvent) => {
    if (!tap.current) return
    tap.current = false
    if ((e.target as HTMLElement).closest('.g-overlay-item, .g-editor')) return
    const h = hit(e.clientX, e.clientY)
    p.onTap(h.r, h.c, h.area)
  }

  const onDoubleClick = (e: React.MouseEvent) => {
    // a finger's second tap is a tap (onClick)
    if (!fine || (e.nativeEvent as MouseEvent & { sourceCapabilities?: { firesTouchEvents?: boolean } }).sourceCapabilities?.firesTouchEvents) return
    const h = hit(e.clientX, e.clientY)
    if (h.area === 'cell') p.onSelect({ ar: h.r, ac: h.c, fr: h.r, fc: h.c }, { drag: false, area: 'cell', double: true })
  }

  const onContextMenu = (e: React.MouseEvent) => {
    // a finger held down: the menu, not a tap
    tap.current = false
    if ((e.target as HTMLElement).closest('.g-editor')) return
    e.preventDefault()
    const h = hit(e.clientX, e.clientY)
    const inside = h.r >= selRect.r1 && h.r <= selRect.r2 && h.c >= selRect.c1 && h.c <= selRect.c2
    if (!inside && h.area !== 'corner') p.onSelect(selFor(h.area, h.r, h.c, null), { drag: false, area: h.area })
    p.onMenu(e.clientX, e.clientY, h.area, h.r, h.c)
  }

  // the editor: over the active cell (kept in view while not editing)
  const ed = p.editing
    ? { left: active.left, top: active.top, minWidth: Math.max(active.width, 120), height: 'auto' as const }
    : {
        left: Math.min(Math.max(active.left, HEAD_W), scroll.vw - 20),
        top: Math.min(Math.max(active.top, HEAD_H), scroll.vh - 20),
        width: 4,
        height: 4,
      }

  const info: ViewInfo = { sx: scroll.sx, sy: scroll.sy, vw: scroll.vw, vh: scroll.vh, xs, frozenW, frozenH }

  return (
    <div className="g-scroll" ref={scroller} onScroll={onScroll} role="grid" aria-label="표" aria-rowcount={rowCount} aria-colcount={colCount}>
      <div className="g-size" style={{ width: totalW, height: totalH }}>
        <div
          className="g-view"
          ref={view}
          style={{ width: scroll.vw, height: scroll.vh }}
          onPointerDown={onPointerDown}
          onPointerCancel={() => (tap.current = false)}
          onClick={onClick}
          onDoubleClick={onDoubleClick}
          onContextMenu={onContextMenu}
        >
          {panes.map((pn) => (
            <div key={pn.key} className="g-pane" style={{ left: pn.clip[0], top: pn.clip[1], width: Math.max(0, pn.clip[2]), height: Math.max(0, pn.clip[3]) }}>
              <div className="g-pane-in" style={{ transform: `translate(${-pn.clip[0]}px, ${-pn.clip[1]}px)` }}>
                {paneCells(pn.rs, pn.cs)}
                {decorations}
                {pn.key === 'br' && p.overlay?.(info)}
              </div>
            </div>
          ))}
          <div className="g-heads-c" style={{ height: HEAD_H }}>
            <div className="g-pane" style={{ left: bodyX, top: 0, width: Math.max(0, scroll.vw - bodyX), height: HEAD_H }}>
              <div className="g-pane-in" style={{ transform: `translate(${-bodyX}px, 0)` }}>
                {cols.map(colHead)}
              </div>
            </div>
            {fc > 0 && (
              <div className="g-pane" style={{ left: HEAD_W, top: 0, width: frozenW, height: HEAD_H }}>
                <div className="g-pane-in" style={{ transform: `translate(${-HEAD_W}px, 0)` }}>
                  {frozenCols.map(colHead)}
                </div>
              </div>
            )}
          </div>
          <div className="g-heads-r" style={{ width: HEAD_W }}>
            <div className="g-pane" style={{ left: 0, top: bodyY, width: HEAD_W, height: Math.max(0, scroll.vh - bodyY) }}>
              <div className="g-pane-in" style={{ transform: `translate(0, ${-bodyY}px)` }}>
                {rows.map(rowHead)}
              </div>
            </div>
            {fr > 0 && (
              <div className="g-pane" style={{ left: 0, top: HEAD_H, width: HEAD_W, height: frozenH }}>
                <div className="g-pane-in" style={{ transform: `translate(0, ${-HEAD_H}px)` }}>
                  {frozenRows.map(rowHead)}
                </div>
              </div>
            )}
          </div>
          <div className="g-corner" style={{ width: HEAD_W, height: HEAD_H }} title="모두 고르기" />
          {fr > 0 && <div className="g-freeze-h" style={{ top: bodyY - 1 }} />}
          {fc > 0 && <div className="g-freeze-v" style={{ left: bodyX - 1 }} />}
          {fine && (
            <textarea
              {...p.editorProps}
              ref={p.editorRef}
              className={`g-editor${p.editing ? ' on' : ''}`}
              style={ed}
              rows={1}
              spellCheck={false}
              autoComplete="off"
              aria-label="칸 편집"
            />
          )}
        </div>
      </div>
    </div>
  )
}
